import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import type { WarrantyExtensionModalProps } from '@/components/Cms/Event/WarrantyExtensionModal';
import { ContactUsModal } from '@/components/ContactUsModal';
import { PriceModal } from '@/components/PriceModal';
import type { ContentfulEventFragment } from '@/graphql/contentful/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { EVENT_NAME } from '@/lib/gtm/const';
import { getTaxMessageDetail } from '@/lib/regions';
import type { Maybe, WithChildren } from '@/lib/types';
import { commonMessages, ContactUsNotice } from '@/messages/common';

import { useRegionProvider } from '../RegionProvider';
import { Provider } from './CommonProviderContext';

export type CommonModalProviderProps<T> = {
  NotifyMeComponent: React.ComponentType<T>; // This should be a component that accepts the specified props
  WarrantyExtensionComponent: React.ComponentType<WarrantyExtensionModalProps>;
};

const GetMyNFTModal = dynamic(() =>
  import('@/components/Cms/Event/GetMyNFTModal').then(
    (mod) => mod.GetMyNFTModal,
  ),
);

const CampaignSubscriptionModal = dynamic(() =>
  import('@/components/Cms/Event/CampaignSubscriptionModal').then(
    (mod) => mod.CampaignSubscriptionModal,
  ),
);

export const CommonModalProvider = <T extends object>({
  NotifyMeComponent,
  WarrantyExtensionComponent,
  children,
}: WithChildren & CommonModalProviderProps<T>) => {
  const { formatMessage } = useIntl();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalProps, setModalProps] = useState<T | undefined>(undefined);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState<boolean>(false);
  const [isContactUsModalOpen, setIsContactUsModalOpen] =
    useState<boolean>(false);
  const [isMyNFTModalOpen, setIsMyNFTModalOpen] = useState<boolean>(false);
  const { sendEvent } = useGoogleAnalytics();
  const [currentSku, setCurrentSku] = useState<Maybe<string>>();
  const [isCampaignNewsletterModalOpen, setIsCampaignNewsletterModalOpen] =
    useState<boolean>(false);
  const [isWarrantyExtensionModalOpen, setIsWarrantyExtensionModalOpen] =
    useState<boolean>(false);

  const openNotifyMe = (props: T) => {
    setIsModalOpen(true);
    setModalProps(props);
  };

  const closeNotifyMe = () => {
    setIsModalOpen(false);
    setModalProps(undefined);
  };

  const setPriceModalStatus = (show: boolean) => {
    setIsPriceModalOpen(show);
  };

  const setContactUsModalStatus = (show: boolean, sku: Maybe<string>) => {
    if (show) {
      sendEvent({
        event: EVENT_NAME.CONTACT,
        contact_type: 'phone',
      });
    }
    if (sku) setCurrentSku(sku);
    setIsContactUsModalOpen(show);
  };

  const setGetMyNFTModalStatus = (show: boolean) => {
    setIsMyNFTModalOpen(show);
  };

  const closeGetMyNFTModal = () => {
    setIsMyNFTModalOpen(false);
  };

  const openCampaignNewsletterModal = (props: T) => {
    setModalProps(props);
    setIsCampaignNewsletterModalOpen(true);
  };

  const closeCampaignNewsletterModalModal = () => {
    setIsCampaignNewsletterModalOpen(false);
    setModalProps(undefined);
  };

  const setWarrantyExtensionModalStatus = (show: boolean) => {
    setIsWarrantyExtensionModalOpen(show);
  };

  const { query } = useRegionProvider();
  const taxMessage = getTaxMessageDetail(query.channel);

  return (
    <Provider
      value={{
        openNotifyMe,
        closeNotifyMe,
        setPriceModalStatus,
        setContactUsModalStatus,
        setGetMyNFTModalStatus,
        openCampaignNewsletterModal,
        setWarrantyExtensionModalStatus,
      }}
    >
      {children}
      <NotifyMeComponent
        {...(modalProps || ({} as any))}
        isOpen={isModalOpen}
        onClose={closeNotifyMe}
      />
      <PriceModal
        isOpen={isPriceModalOpen}
        onClose={() => setIsPriceModalOpen(false)}
      >
        {taxMessage &&
          formatMessage(commonMessages.priceVatNotice, {
            taxMessage: formatMessage(taxMessage),
          })}
      </PriceModal>
      <ContactUsModal
        isOpen={isContactUsModalOpen}
        onClose={() => setIsContactUsModalOpen(false)}
        sku={currentSku}
      >
        <ContactUsNotice />
      </ContactUsModal>
      <GetMyNFTModal isOpen={isMyNFTModalOpen} onClose={closeGetMyNFTModal} />
      <CampaignSubscriptionModal
        isOpen={isCampaignNewsletterModalOpen}
        onClose={closeCampaignNewsletterModalModal}
        urlTrigger={modalProps as ContentfulEventFragment}
      />
      <WarrantyExtensionComponent
        isOpen={isWarrantyExtensionModalOpen}
        onClose={() => setWarrantyExtensionModalStatus(false)}
      />
    </Provider>
  );
};
